import type { IdentityFormData, KycEmployedActivityRow, KycSelfEmployedActivityRow, ValidationError } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";

/**
 * Reglas de cliente.
 *  - la requerida completa vive en el backend (ProcessAndSync); aquí solo se replica para
 *    la UX del formulario (habilitar botones y resaltar pendientes);
 *  - los bloqueos de alta se anticipan en cliente para los booleanos (respuesta inmediata),
 *    pero la decisión final siempre es del servidor (D1: se persiste y después se bloquea).
 */

// Valor del enum legacy MaritalStatuses correspondiente a "Casado" (backend: Married = 1).
export const MaritalStatusMarriedValue = 1;

// Campos del formulario (name) que pertenecen al bloque KYC y sus tipos de parseo en handleChange.
export const KycStringCodeFields = ["annualGrossIncome", "fundsOrigin", "fundsDocumentationProvided", "otherPersonalCircumstances", "maritalSituation"] as const;
export const KycBooleanFields = ["hasEmployedActivity", "hasSelfEmployedActivity", "internationalCommercialActivity", "isPepRelative", "otherNationalities", "riskCountries", "riskJurisdictionActivity", "isUsCitizen"] as const;

// Aviso operativo reutilizado para todas las respuestas que implican "US person" (residencia,
// identificación fiscal u obligación tributaria en EEUU, o ciudadanía estadounidense): por
// normativa no se admiten intervinientes en ninguno de esos casos.
export const US_PERSON_DISCLAIMER_TEXT =
    "Debido a la operatividad de nuestras normas vigentes no permitimos operar con intervinientes cuyo país de residencia sea Estados Unidos. " +
    "Selecciona un país de residencia distinto para continuar con el proceso.";

// La condición de persona con responsabilidad pública se pregunta dos veces (publicPosition en
// Información Financiera e isPepRelative en el bloque KYC): ambas comparten el mismo aviso.
export const PEP_DISCLAIMER_TEXT =
    "Debido a la operatividad de nuestras normas vigentes no permitimos operar con personas con responsabilidad pública ni con sus familiares o allegados. " +
    "Mientras la respuesta sea afirmativa no es posible guardar ni continuar con el proceso.";

function hasCode(value: string | null | undefined): boolean
{
    return typeof value === "string" && value.trim() !== "";
}

function isAnswered(value: boolean | undefined | null): boolean
{
    return value === true || value === false;
}

export function isEmployedRowComplete(row: KycEmployedActivityRow): boolean
{
    return hasCode(row.activityStatus) && hasCode(row.cnoCode) && row.years !== null && row.years !== undefined && row.years >= 0 && hasCode(row.employedCurrentCompany);
}

export function isSelfEmployedRowComplete(row: KycSelfEmployedActivityRow): boolean
{
    return hasCode(row.activityStatus) && hasCode(row.cnaeCode) && row.years !== null && row.years !== undefined && row.years >= 0 && hasCode(row.relatedProfessionalActivity);
}

/** Validez de una puerta de actividad + sus filas (gate contestada; true exige ≥1 fila completa y sin duplicados). */
function isActivityBlockValid(gate: boolean | undefined, rowsComplete: boolean, rowCount: number, hasDuplicates: boolean): boolean
{
    if(!isAnswered(gate))
    {
        return false;
    }
    if(gate === false)
    {
        return true;
    }
    return rowCount > 0 && rowsComplete && !hasDuplicates;
}

// ─── Duplicados de actividades (espejo de KYC_DUPLICATED_* del backend) ─────

function normalizeCode(code: string | null | undefined): string
{
    return (code ?? "").trim().toUpperCase();
}

function hasDuplicates(keys: string[]): boolean
{
    return new Set(keys).size !== keys.length;
}

/** Dos actividades por cuenta ajena idénticas en todas sus opciones no están permitidas. */
export function hasDuplicatedEmployedActivities(rows: KycEmployedActivityRow[]): boolean
{
    return hasDuplicates(rows.map((row) => `${normalizeCode(row.activityStatus)}|${normalizeCode(row.cnoCode)}|${row.years ?? ""}|${normalizeCode(row.employedCurrentCompany)}`));
}

/** Dos actividades por cuenta propia idénticas en todas sus opciones no están permitidas. */
export function hasDuplicatedSelfEmployedActivities(rows: KycSelfEmployedActivityRow[]): boolean
{
    return hasDuplicates(rows.map((row) => `${normalizeCode(row.activityStatus)}|${normalizeCode(row.cnaeCode)}|${row.years ?? ""}|${normalizeCode(row.relatedProfessionalActivity)}`));
}

export interface KycValidationSlice
{
    annualGrossIncome: boolean;
    hasEmployedActivity: boolean;
    hasSelfEmployedActivity: boolean;
    employedActivities: boolean;
    selfEmployedActivities: boolean;
    fundsOrigin: boolean;
    fundsDocumentationProvided: boolean;
    internationalCommercialActivity: boolean;
    isPepRelative: boolean;
    numberOfDependants: boolean;
    otherNationalities: boolean;
    otherNationality: boolean;
    otherPersonalCircumstances: boolean;
    riskCountries: boolean;
    riskJurisdictionActivity: boolean;
    maritalSituation: boolean;
    isUsCitizen: boolean;
}

/**
 * Recalcula la validación de todos los campos KYC a partir del estado del formulario.
 * `allowedDocumentation` es la matriz origen→documentaciones del catálogo (para invalidar
 * combinaciones no permitidas sin duplicar la regla de negocio).
 */
export function getKycValidation(data: IdentityFormData, allowedDocumentation: Record<string, string[]> | null | undefined): KycValidationSlice
{
    const employedRowsComplete = data.employedActivities.every(isEmployedRowComplete);
    const selfEmployedRowsComplete = data.selfEmployedActivities.every(isSelfEmployedRowComplete);

    const documentationAllowed = !hasCode(data.fundsDocumentationProvided) ||
        (hasCode(data.fundsOrigin) &&
        (!allowedDocumentation || (allowedDocumentation[data.fundsOrigin as string] ?? []).includes(data.fundsDocumentationProvided as string)));

    const isMarried = data.maritalStatus === MaritalStatusMarriedValue;

    return{
        annualGrossIncome: hasCode(data.annualGrossIncome),
        hasEmployedActivity: isAnswered(data.hasEmployedActivity),
        hasSelfEmployedActivity: isAnswered(data.hasSelfEmployedActivity),
        employedActivities: isActivityBlockValid(data.hasEmployedActivity, employedRowsComplete, data.employedActivities.length, hasDuplicatedEmployedActivities(data.employedActivities)),
        selfEmployedActivities: isActivityBlockValid(data.hasSelfEmployedActivity, selfEmployedRowsComplete, data.selfEmployedActivities.length, hasDuplicatedSelfEmployedActivities(data.selfEmployedActivities)),
        fundsOrigin: hasCode(data.fundsOrigin),
        fundsDocumentationProvided: hasCode(data.fundsDocumentationProvided) && documentationAllowed,
        internationalCommercialActivity: isAnswered(data.internationalCommercialActivity),
        isPepRelative: isAnswered(data.isPepRelative),
        numberOfDependants: data.numberOfDependants !== null && data.numberOfDependants !== undefined && data.numberOfDependants >= 0,
        otherNationalities: isAnswered(data.otherNationalities),
        otherNationality: data.otherNationalities !== true || hasCode(data.otherNationality),
        otherPersonalCircumstances: hasCode(data.otherPersonalCircumstances),
        riskCountries: isAnswered(data.riskCountries),
        riskJurisdictionActivity: isAnswered(data.riskJurisdictionActivity),
        maritalSituation: !isMarried || hasCode(data.maritalSituation),
        isUsCitizen: isAnswered(data.isUsCitizen),
    };
}

/** Completitud del bloque KYC (espejo de ValidateCompleteness del backend, para modo edición). */
export function isKycComplete(data: IdentityFormData, allowedDocumentation: Record<string, string[]> | null | undefined): boolean
{
    const validation = getKycValidation(data, allowedDocumentation);
    return Object.values(validation).every((valid) => valid === true);
}

// ─── Bloqueos de alta anticipados en cliente ─────────────────────────────────

export interface KycLocalBlock
{
    /** Mismo código que devuelve el servidor, para anclar en el mismo campo. */
    code: string;
    message: string;
    /** Además de la finalización, impide guardar el borrador (excepción a D1). */
    preventsSave?: boolean;
}

/**
 * Respuestas que el backend rechazará al formalizar (KYC_BLOCKED_*). Se anticipan en cliente
 * para dar respuesta inmediata en el campo. La nacionalidad bloqueante NO se replica (la lista
 * de países vive solo en el servidor): ese bloqueo llega por errors[] al guardar.
 */
/**
 * Vínculos con EEUU declarados en el formulario: residencia fiscal, identificación fiscal, obligación
 * tributaria o ciudadanía.
 *
 * El estado FATCA no se evalúa aquí porque el literal permitido ("No sujeto a comunicación FATCA") solo
 * se puede resolver con el catálogo, que no llega a este módulo. No hace falta: cualquier otro estado ya
 * invalida el formulario completo por la vía de isFiscalBlocked, así que el resultado es el mismo.
 */
export function hasDeclaredUsTies(data: IdentityFormData): boolean
{
    return data.residenceCountry === "US" ||
        data.hasTaxIdInUS === true ||
        data.hasResidenceInUS === true ||
        data.hasTaxObligationInUS === true ||
        data.isUsCitizen === true;
}

/**
 * La actividad comercial internacional (importación/exportación, cobros o pagos con el exterior) NO
 * impide por sí sola el alta: es operativa habitual y lícita. Solo bloquea combinada con vínculos
 * declarados con EEUU o con una respuesta afirmativa a la pregunta GAFI, que es donde Negocio sitúa el
 * riesgo. Espejo de KycFieldRules.IsInternationalActivityBlocking del backend.
 */
export function isInternationalActivityBlocking(data: IdentityFormData): boolean
{
    if(data.internationalCommercialActivity !== true)
    {
        return false;
    }
    return hasDeclaredUsTies(data) || data.gafiQuestion === true;
}

export function getKycLocalBlocks(data: IdentityFormData): KycLocalBlock[]
{
    const blocks: KycLocalBlock[] = [];

    if(isInternationalActivityBlocking(data))
    {
        blocks.push({code: "KYC_BLOCKED_INTERNATIONAL_ACTIVITY", message: "La actividad comercial internacional declarada, combinada con los vínculos con EEUU o con la respuesta GAFI afirmativa, no permite continuar con el alta."});
    }
    if(data.publicPosition === true)
    {
        blocks.push({code: "KYC_BLOCKED_PUBLIC_POSITION", message: PEP_DISCLAIMER_TEXT, preventsSave: true});
    }
    if(data.isPepRelative === true)
    {
        blocks.push({code: "KYC_BLOCKED_PEP_RELATIVE", message: PEP_DISCLAIMER_TEXT, preventsSave: true});
    }
    if(data.riskCountries === true)
    {
        blocks.push({code: "KYC_BLOCKED_RISK_COUNTRIES", message: "La operativa con países de riesgo declarada no permite continuar con el alta."});
    }
    if(data.riskJurisdictionActivity === true)
    {
        blocks.push({code: "KYC_BLOCKED_RISK_JURISDICTION", message: "La actividad en jurisdicciones de riesgo declarada no permite continuar con el alta."});
    }
    if(data.isUsCitizen === true)
    {
        blocks.push({code: "KYC_BLOCKED_US_CITIZEN", message: US_PERSON_DISCLAIMER_TEXT});
    }

    return blocks;
}

/** Respuestas que impiden incluso guardar el borrador (persona con responsabilidad pública). */
export function hasSaveBlockingKycAnswers(data: IdentityFormData): boolean
{
    return getKycLocalBlocks(data).some((block) => block.preventsSave === true);
}

// ─── Anclaje de errores del servidor por campo ───────────────────────────────

/**
 * Campo del bloque KYC en el que se ancla cada código de error del servidor.
 * Los códigos no listados se muestran solo en el listado general de errores.
 */
const KycServerErrorFieldByCode: Record<string, keyof KycValidationSlice> =
{
    // Bloqueos de alta (KycFieldRules)
    KYC_BLOCKED_INTERNATIONAL_ACTIVITY: "internationalCommercialActivity",
    KYC_BLOCKED_RISK_COUNTRIES: "riskCountries",
    KYC_BLOCKED_RISK_JURISDICTION: "riskJurisdictionActivity",
    KYC_BLOCKED_NATIONALITY: "otherNationality",
    KYC_BLOCKED_PEP_RELATIVE: "isPepRelative",
    // Escalares (KycBusinessRules)
    KYC_ANNUAL_GROSS_INCOME_REQUIRED: "annualGrossIncome",
    KYC_INVALID_ANNUAL_GROSS_INCOME: "annualGrossIncome",
    KYC_HAS_EMPLOYED_ACTIVITY_REQUIRED: "hasEmployedActivity",
    KYC_HAS_SELF_EMPLOYED_ACTIVITY_REQUIRED: "hasSelfEmployedActivity",
    KYC_FUNDS_ORIGIN_REQUIRED: "fundsOrigin",
    KYC_INVALID_FUNDS_ORIGIN: "fundsOrigin",
    KYC_FUNDS_DOCUMENTATION_REQUIRED: "fundsDocumentationProvided",
    KYC_INVALID_FUNDS_DOCUMENTATION: "fundsDocumentationProvided",
    KYC_FUNDS_DOCUMENTATION_NOT_ALLOWED: "fundsDocumentationProvided",
    KYC_INTERNATIONAL_ACTIVITY_REQUIRED: "internationalCommercialActivity",
    KYC_IS_PEP_RELATIVE_REQUIRED: "isPepRelative",
    KYC_DEPENDANTS_REQUIRED: "numberOfDependants",
    KYC_NEGATIVE_DEPENDANTS: "numberOfDependants",
    KYC_OTHER_NATIONALITIES_REQUIRED: "otherNationalities",
    KYC_OTHER_NATIONALITY_REQUIRED: "otherNationality",
    KYC_INCOHERENT_OTHER_NATIONALITY: "otherNationality",
    KYC_PERSONAL_CIRCUMSTANCES_REQUIRED: "otherPersonalCircumstances",
    KYC_INVALID_PERSONAL_CIRCUMSTANCES: "otherPersonalCircumstances",
    KYC_RISK_COUNTRIES_REQUIRED: "riskCountries",
    KYC_RISK_JURISDICTION_REQUIRED: "riskJurisdictionActivity",
    KYC_MARITAL_SITUATION_REQUIRED: "maritalSituation",
    KYC_INVALID_MARITAL_SITUATION: "maritalSituation",
    KYC_INCOHERENT_MARITAL_SITUATION: "maritalSituation",
    // Colecciones de actividades (puerta + filas)
    KYC_DUPLICATED_EMPLOYED_ACTIVITIES: "employedActivities",
    KYC_DUPLICATED_SELF_EMPLOYED_ACTIVITIES: "selfEmployedActivities",
    KYC_EMPLOYED_ACTIVITIES_REQUIRED: "employedActivities",
    KYC_EMPLOYED_ACTIVITIES_WITHOUT_FLAG: "employedActivities",
    KYC_INCOHERENT_EMPLOYED_ACTIVITIES: "employedActivities",
    KYC_EMPLOYED_ACTIVITY_STATUS_REQUIRED: "employedActivities",
    KYC_EMPLOYED_ACTIVITY_CNO_REQUIRED: "employedActivities",
    KYC_EMPLOYED_ACTIVITY_YEARS_REQUIRED: "employedActivities",
    KYC_EMPLOYED_CURRENT_COMPANY_REQUIRED: "employedActivities",
    KYC_SELF_EMPLOYED_ACTIVITIES_REQUIRED: "selfEmployedActivities",
    KYC_SELF_EMPLOYED_ACTIVITIES_WITHOUT_FLAG: "selfEmployedActivities",
    KYC_INCOHERENT_SELF_EMPLOYED_ACTIVITIES: "selfEmployedActivities",
    KYC_SELF_EMPLOYED_ACTIVITY_STATUS_REQUIRED: "selfEmployedActivities",
    KYC_SELF_EMPLOYED_ACTIVITY_CNAE_REQUIRED: "selfEmployedActivities",
    KYC_SELF_EMPLOYED_ACTIVITY_YEARS_REQUIRED: "selfEmployedActivities",
    KYC_RELATED_PROFESSIONAL_ACTIVITY_REQUIRED: "selfEmployedActivities",
    KYC_INVALID_RELATED_PROFESSIONAL_ACTIVITY: "selfEmployedActivities",
    // KYC_INVALID_ACTIVITY_STATUS y KYC_NEGATIVE_YEARS pueden venir de cualquiera de las dos
    // colecciones: se anclan a la de cuenta ajena por defecto (aparecen también en el listado general).
    KYC_INVALID_ACTIVITY_STATUS: "employedActivities",
    KYC_NEGATIVE_YEARS: "employedActivities",
};

/**
 * Devuelve, por campo KYC, los mensajes del servidor a anclar bajo él. `errors` es la lista
 * ya mapeada del envelope (field = code). Un mismo campo puede acumular varios mensajes.
 */
export function getKycServerErrorsByField(errors: ValidationError[]): Partial<Record<keyof KycValidationSlice, string[]>>
{
    const byField: Partial<Record<keyof KycValidationSlice, string[]>> = {};
    for(const error of errors)
    {
        const field = KycServerErrorFieldByCode[error.field];
        if(!field)
        {
            continue;
        }
        const list = byField[field] ?? [];
        list.push(error.message);
        byField[field] = list;
    }
    return byField;
}
